import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Loader2 } from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/components/ui/use-toast'

interface InviteAthleteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaignId: string
}

export const InviteAthleteModal: React.FC<InviteAthleteModalProps> = ({
  open,
  onOpenChange,
  campaignId,
}) => {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')

  const handleClose = () => {
    setEmail('')
    setMessage('')
    onOpenChange(false)
  }

  const handleSend = async () => {
    if (!email.trim()) return

    setLoading(true)
    try {
      await api.post(`/marketer/campaigns/${campaignId}/invitations`, {
        email: email.trim(),
        message: message.trim() || undefined,
      })
      toast({ title: 'Invitation sent', description: `An invite was sent to ${email.trim()}` })
      handleClose()
    } catch (error) {
      console.error('Error sending athlete invitation:', error)
      toast({ title: 'Error', description: 'Failed to send invitation', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const inputClassName =
    'border-foreground/15 bg-card text-foreground placeholder:text-gray-500 focus-visible:ring-[#C0FD72]/40 focus-visible:ring-offset-0'

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md border-foreground/15 bg-card text-foreground">
        <DialogHeader className="border-b border-foreground/10 pb-4">
          <DialogTitle className="text-foreground">Invite Athlete to Athios</DialogTitle>
          <DialogDescription className="text-gray-600">
            Send an email invitation to an athlete. Once they join, you'll be notified so you can
            add them to this campaign.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="athlete-email" className="text-gray-700">
              Athlete's Email
            </Label>
            <Input
              id="athlete-email"
              type="email"
              placeholder="athlete@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClassName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend()
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite-message" className="text-gray-700">
              Personal Message <span className="text-gray-500">(optional)</span>
            </Label>
            <Textarea
              id="invite-message"
              placeholder="Tell them why you'd like them to join..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className={inputClassName}
            />
          </div>
        </div>

        <DialogFooter className="border-t border-foreground/10 pt-4">
          <Button variant="dark-outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="lime" onClick={handleSend} disabled={loading || !email.trim()}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send Invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
